/**
 * MonetaryAmountInput - Combined currency picker and localized amount input
 *
 * A reusable component for monetary amount entry that combines:
 * - CurrencyPicker: For selecting currency (ISO 4217 codes)
 * - LocalizedMonetaryInput: For entering amounts with locale-aware decimal separators
 */

import { cn } from '../../../lib/utils';
import { CurrencyPicker, getCurrencySymbol } from './CurrencyPicker';
import { LocalizedMonetaryInput } from '../LocalizedMonetaryInput';

export interface MonetaryAmountInputProps {
  /** Currency code (ISO 4217, e.g., 'USD', 'EUR') */
  currency: string | null;
  /** Amount value (in the display unit, e.g., dollars not cents) */
  amount: number | null | undefined;
  /** Called when currency changes */
  onCurrencyChange: (currency: string | null) => void;
  /** Called when amount changes */
  onAmountChange: (amount: number | null) => void;
  /** Label for the currency picker */
  currencyLabel?: string;
  /** Label for the amount input */
  amountLabel?: string;
  /** Whether the fields are required */
  required?: boolean;
  /** Whether the fields are disabled */
  disabled?: boolean;
  /** Error state for the amount field */
  amountError?: boolean;
  /** Error message for the amount field */
  amountErrorMessage?: string;
  /** Error state for the currency field */
  currencyError?: boolean;
  /** Error message for the currency field */
  currencyErrorMessage?: string;
  /** Optional list of allowed currencies (if not provided, shows all) */
  allowedCurrencies?: string[];
  /**
   * Pre-fill the currency with the viewer's default when none is set (on by
   * default). Pass `false` where a blank currency is meaningful — see
   * `CurrencyPicker`.
   */
  autoSelectDefaultCurrency?: boolean;
  /** Size variant */
  size?: 'sm' | 'default';
  /** Whether to take full width */
  fullWidth?: boolean;
  /** Additional className for the container */
  className?: string;
  /** Width of the currency picker (default: 100px) */
  currencyWidth?: number;
}

export function MonetaryAmountInput({
  currency,
  amount,
  onCurrencyChange,
  onAmountChange,
  currencyLabel,
  amountLabel,
  required = false,
  disabled = false,
  amountError = false,
  amountErrorMessage,
  currencyError = false,
  currencyErrorMessage,
  allowedCurrencies,
  autoSelectDefaultCurrency = true,
  size = 'default',
  fullWidth = true,
  className,
  currencyWidth = 120,
}: MonetaryAmountInputProps) {
  // Get currency symbol for display in the amount field
  const currencySymbol = currency ? getCurrencySymbol(currency) : undefined;

  // Align inputs at bottom when only one has a label (most common case: amountLabel only)
  const hasLabelMismatch = (amountLabel && !currencyLabel) || (!amountLabel && currencyLabel);

  return (
    <div
      className={cn(
        'flex gap-2',
        hasLabelMismatch ? 'items-end' : 'items-start',
        fullWidth && 'w-full',
        className
      )}
    >
      <CurrencyPicker
        value={currency}
        onChange={onCurrencyChange}
        label={currencyLabel}
        required={required}
        disabled={disabled}
        error={currencyError}
        errorMessage={currencyErrorMessage}
        allowedCurrencies={allowedCurrencies}
        autoSelectDefaultCurrency={autoSelectDefaultCurrency}
        compact
        size={size}
        fullWidth={false}
        className="shrink-0 overflow-hidden [&_button]:overflow-hidden [&_button]:text-ellipsis"
        style={{ width: currencyWidth }}
      />
      <LocalizedMonetaryInput
        value={amount ?? null}
        onChange={onAmountChange}
        label={amountLabel}
        currency={currencySymbol}
        currencyPosition="start"
        required={required}
        disabled={disabled}
        error={amountError}
        helperText={amountErrorMessage}
        size={size}
        fullWidth
      />
    </div>
  );
}
